import { isNative } from './env';
import { registerPlugin } from '@capacitor/core';

// Bind directly to the native InAppBrowser plugin via Capacitor's runtime.
// The previous attempt that did `require('@capacitor/inappbrowser/dist/plugin.cjs')`
// resolved a stale/empty proxy on iOS at runtime — `openInWebView` came back
// undefined. `registerPlugin('InAppBrowser')` returns the canonical proxy
// Capacitor builds for any plugin by name, regardless of which package's
// minified bundle webpack 4 ends up loading.
const InAppBrowser = registerPlugin('InAppBrowser');

// The plugin requires every WebViewOptions field to be present (no
// shorthand) — partial objects fail with OS-PLUG-IABP-0007. We use the
// upstream defaults and override only what we need. Keeping these inline
// rather than importing `DefaultWebViewOptions` from the package so webpack
// doesn't have to resolve another deep export path.
const TOOLBAR_TOP = 0; // ToolbarPosition.TOP
const IOS_FULL_SCREEN = 2; // iOSViewStyle.FULL_SCREEN
const IOS_COVER_VERTICAL = 2; // iOSAnimation.COVER_VERTICAL

const DEFAULT_WEBVIEW_OPTIONS = {
    showURL: false,
    showToolbar: true,
    clearCache: false,
    clearSessionCache: false,
    mediaPlaybackRequiresUserAction: false,
    closeButtonText: 'סגור',
    toolbarPosition: TOOLBAR_TOP,
    showNavigationButtons: false,
    leftToRight: false,
    customWebViewUserAgent: null,
    android: {
        allowZoom: false,
        hardwareBack: true,
        pauseMedia: true,
        isIsolated: true,
    },
    iOS: {
        allowOverScroll: false,
        enableViewportScale: false,
        allowInLineMediaPlayback: false,
        surpressIncrementalRendering: false,
        viewStyle: IOS_FULL_SCREEN,
        animationEffect: IOS_COVER_VERTICAL,
        allowsBackForwardNavigationGestures: true,
    },
};

// In-app browser wrapper. Native opens a WKWebView (iOS) / WebView (Android)
// via @capacitor/inappbrowser. Unlike SFSafariViewController + Chrome Custom
// Tabs (the previous @capacitor/browser implementation), this exposes a
// `urlChangeEvent` listener — required so we can intercept the Yaad Sarig
// redirect to https://advice-ev.com/updateBilling?... without any backend
// change to the success URL. On web it falls back to a full-page assign.

export async function openInAppBrowser(url, options = {}) {
    if (!url) return;
    if (!isNative) {
        window.location.assign(url); // preserves existing web behavior
        return;
    }
    await InAppBrowser.openInWebView({
        url,
        options: { ...DEFAULT_WEBVIEW_OPTIONS, ...(options.overrides || {}) },
    });
}

export async function closeInAppBrowser() {
    if (!isNative) return;
    try { await InAppBrowser.close(); } catch { /* already closed */ }
}

// Fires when the user dismisses the in-app browser themselves (close button /
// swipe). Used by the payment flow to clear loading state if the user cancels.
export async function bindBrowserFinished(handler) {
    if (!isNative) return () => {};
    const sub = await InAppBrowser.addListener('browserClosed', handler);
    return () => { try { sub.remove?.(); } catch {} };
}

// Fires after every page navigation finishes inside the in-app browser.
// Used by deepLink.js to detect the Yaad Sarig payment-return URL (presence
// of CCode + UID + L4digit query params) and route back into the app.
//
// `@capacitor/inappbrowser` v4's event name is `browserPageNavigationCompleted`
// and the payload is `{ url }`. We don't get a "navigation started" event,
// so the page loads briefly before we close it — acceptable for the success
// step since the destination is just our own SPA URL.
export async function bindInAppBrowserUrlChange(handler) {
    if (!isNative) return () => {};
    const sub = await InAppBrowser.addListener('browserPageNavigationCompleted', handler);
    return () => { try { sub.remove?.(); } catch {} };
}
